import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ShoppingBag, User, Heart } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { TricolorStripeMini } from './TricolorStripe';
import { SearchBar } from './SearchBar';

// Announcement Bar Component
const AnnouncementBar = () => {
  const { lang } = useLanguage();
  return (
    <div className="bg-[#CE2B37] text-white h-7 flex items-center justify-center fixed top-0 left-0 right-0 z-[60]" data-testid="announcement-bar">
      <p className="font-dm text-[10px] md:text-xs tracking-wide px-4">
        {lang === 'en' 
          ? '🔥 2026 Summer Collections Now Available — Shop the Latest Italian Designs'
          : '🔥 Collections Été 2026 Maintenant Disponibles — Découvrez les Derniers Designs Italiens'}
      </p>
    </div>
  );
};

export const Header = () => {
  const { lang, toggleLanguage, t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 80);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { path: '/', label: t.nav.home },
    { path: '/catalogue', label: t.nav.catalogue },
    { path: '/custom-uniforms', label: t.nav.customUniforms },
    { path: '/clubs', label: t.nav.clubs },
    { path: '/macron', label: t.nav.macron },
    { path: '/about', label: t.nav.about },
    { path: '/contact', label: t.nav.contact },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {/* Announcement Bar */}
      <AnnouncementBar />
      
      <header
        className={`fixed top-7 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-[#0D0D0D]/95 backdrop-blur-xl border-b border-white/10'
            : 'bg-transparent'
        }`}
        data-testid="header"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex flex-col" data-testid="header-logo">
              <span className="font-bebas text-[20px] md:text-[24px] leading-none text-white tracking-tight">
                DM SPORTS
              </span>
              <span className="font-dm text-[7px] md:text-[8px] tracking-[2px] md:tracking-[3px] text-[#CE2B37] uppercase">
                MASS CANADA
              </span>
              <TricolorStripeMini width="28px" className="mt-0.5" />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-6" data-testid="desktop-nav">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`font-dm text-xs uppercase tracking-wider transition-colors duration-200 ${
                    isActive(link.path)
                      ? 'text-[#CE2B37]'
                      : 'text-white/80 hover:text-white'
                  }`}
                  data-testid={`nav-link-${link.path.replace('/', '') || 'home'}`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* Right Side - Icons */}
            <div className="flex items-center gap-3">
              {/* Language Toggle */}
              <button
                onClick={toggleLanguage}
                className="font-dm text-xs tracking-wider hidden sm:block"
                data-testid="language-toggle"
              >
                <span className={lang === 'fr' ? 'text-white font-semibold' : 'text-white/50'}>
                  FR
                </span>
                <span className="text-white/50 mx-1">|</span>
                <span className={lang === 'en' ? 'text-white font-semibold' : 'text-white/50'}>
                  EN
                </span>
              </button>

              {/* Icon Buttons */}
              <div className="hidden md:flex items-center gap-2">
                <SearchBar variant="desktop" />
                <button className="p-2 text-white/70 hover:text-white transition-colors" data-testid="wishlist-icon">
                  <Heart size={18} />
                </button>
                <button className="p-2 text-white/70 hover:text-white transition-colors" data-testid="account-icon">
                  <User size={18} />
                </button>
                <button className="p-2 text-white/70 hover:text-white transition-colors" data-testid="cart-icon">
                  <ShoppingBag size={18} />
                </button>
              </div>

              {/* CTA Button - Desktop */}
              <Link
                to="/contact"
                className="hidden lg:block bg-[#CE2B37] text-white font-bebas text-xs tracking-wider px-4 py-2 hover:bg-[#a82230] transition-colors duration-200"
                data-testid="header-cta"
              >
                {t.nav.requestQuote}
              </Link>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden text-white p-2"
                data-testid="mobile-menu-toggle"
              >
                {isMobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <div
        className={`fixed inset-0 z-40 bg-[#0D0D0D] transition-transform duration-300 lg:hidden pt-20 ${
          isMobileMenuOpen ? 'translate-y-0' : '-translate-y-full'
        }`}
        data-testid="mobile-menu"
      >
        <div className="flex flex-col items-center justify-center h-full gap-8">
          <SearchBar variant="mobile" />
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`font-bebas text-3xl tracking-wider ${
                isActive(link.path) ? 'text-[#CE2B37]' : 'text-white'
              }`}
              data-testid={`mobile-nav-link-${link.path.replace('/', '') || 'home'}`}
            >
              {link.label}
            </Link>
          ))}
          <Link
            to="/contact"
            className="mt-8 bg-[#CE2B37] text-white font-bebas text-xl tracking-wider px-10 py-4"
            data-testid="mobile-header-cta"
          >
            {t.nav.requestQuote}
          </Link>
          <button
            onClick={toggleLanguage}
            className="mt-4 font-dm text-lg tracking-wider"
            data-testid="mobile-language-toggle"
          >
            <span className={lang === 'fr' ? 'text-white font-semibold' : 'text-white/50'}>
              FR
            </span>
            <span className="text-white/50 mx-2">|</span>
            <span className={lang === 'en' ? 'text-white font-semibold' : 'text-white/50'}>
              EN
            </span>
          </button>
        </div>
      </div>
    </>
  );
};
